import {
  IArchivingJobExperimentResultDTO,
  ArchivingJobExperimentStatusDto,
} from 'business/models/Models';
import { EJobStatus } from 'business/models/CustomModels';
import { BackgroundJobDTO } from 'components/backgroundjobs/backgroundJobs';
import { normalizeJobDataObj } from 'redux/utils/archivingProgressUtils';
import {
  ARCHIVED_FILES_SECTION_PARAM,
  ARCHIVED_FILES_SECTION_VALUE,
  ARCHIVING_NAME_DUPLICATE_MESSAGE,
  ARCHIVING_NAME_INVALID_CHARACTERS_MESSAGE,
  ARCHIVING_NAME_INVALID_CHARACTERS_REGEX,
  ARCHIVING_PAUSED_BY_USER_STATUS,
  ARCHIVING_STARTED_OVER_STATUS,
  ARCHIVING_STARTING_OVER_STATUS,
  ARCHIVING_STORAGE_FULL_INLINE_MESSAGE,
  ARCHIVING_STORAGE_INVALID_TITLE,
  ARCHIVING_STORAGE_LOW_MESSAGE,
  ARCHIVING_STORAGE_LOW_THRESHOLD_GB,
  ARCHIVING_WAITING_FOR_STORAGE_STATUS,
  EArchivingDashboardTab,
} from './constants';

// Mirrors DB semantics: Pending or Running rows mean an active archiving job exists.
// Used to block UI immediately after POST /api/Archiving/run and on page reload,
// before the SignalR hub delivers an InProgress status update.
export const hasActiveArchivingJobInResults = (
  results: IArchivingJobExperimentResultDTO[],
): boolean =>
  Array.isArray(results) &&
  results.some(
    (r) =>
      r.status === ArchivingJobExperimentStatusDto.Pending ||
      r.status === ArchivingJobExperimentStatusDto.Running,
  );

export const isDismissableArchivingResultStatus = (
  status: ArchivingJobExperimentStatusDto,
) =>
  status === ArchivingJobExperimentStatusDto.Completed ||
  status === ArchivingJobExperimentStatusDto.Failed;

const TIME_24H_REGEX = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/;

export const formatArchivingScheduleTime = (time: string): string => {
  const trimmed = time?.trim() ?? '';
  if (!trimmed) {
    return '';
  }
  const twentyFourMatch = trimmed.match(TIME_24H_REGEX);
  if (!twentyFourMatch) {
    return trimmed;
  }

  let hour = parseInt(twentyFourMatch[1], 10);
  const minute = twentyFourMatch[2];
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12;
  if (hour === 0) {
    hour = 12;
  }

  return `${hour.toString().padStart(2, '0')}:${minute} ${meridiem}`;
};

export type ArchivingNameValidationError = 'required' | 'invalidCharacters' | 'duplicate';

export const getArchivingNameValidationError = (
  name: string,
  existingNames: string[] = [],
): ArchivingNameValidationError | null => {
  const trimmedName = name.trim();
  if (!trimmedName) {
    return 'required';
  }

  if (ARCHIVING_NAME_INVALID_CHARACTERS_REGEX.test(trimmedName)) {
    return 'invalidCharacters';
  }

  if (existingNames.some((existingName) => existingName.trim() === trimmedName)) {
    return 'duplicate';
  }

  return null;
};

export const ARCHIVING_NAME_VALIDATION_MESSAGES: Record<
  ArchivingNameValidationError,
  string
> = {
  required: 'This field is required',
  invalidCharacters: ARCHIVING_NAME_INVALID_CHARACTERS_MESSAGE,
  duplicate: ARCHIVING_NAME_DUPLICATE_MESSAGE,
};

export type ArchivingStorageStatus = 'ok' | 'low' | 'full' | 'invalid';

// Decides whether the resolved archiving location can accept a new job.
// availableSize is in GB: -1 (or missing/empty location) means the drive could not be
// read -> 'invalid'; 0 means no free space -> 'full'; below the low threshold -> 'low'
// (archiving still runs, but the user is warned); anything else -> 'ok'.
export const getArchivingStorageStatus = (
  location:
    | { location?: string | null; availableSize?: number | null }
    | null
    | undefined,
  lowThresholdGb: number = ARCHIVING_STORAGE_LOW_THRESHOLD_GB,
): ArchivingStorageStatus => {
  if (
    !location ||
    !location.location ||
    location.availableSize === undefined ||
    location.availableSize === null ||
    location.availableSize < 0
  ) {
    return 'invalid';
  }

  if (location.availableSize <= 0) {
    return 'full';
  }

  if (location.availableSize < lowThresholdGb) {
    return 'low';
  }

  return 'ok';
};

export type ArchivingStorageNotice = {
  message: string;
  type: 'error' | 'warning';
};

// Maps a storage status to the inline notice shown above the preview table.
// 'full'/'invalid' are errors (block scheduling); 'low' is a non-blocking warning.
export const getArchivingStorageNotice = (
  status: ArchivingStorageStatus,
): ArchivingStorageNotice | null => {
  switch (status) {
    case 'full':
      return { message: ARCHIVING_STORAGE_FULL_INLINE_MESSAGE, type: 'error' };
    case 'invalid':
      return { message: ARCHIVING_STORAGE_INVALID_TITLE, type: 'error' };
    case 'low':
      return { message: ARCHIVING_STORAGE_LOW_MESSAGE, type: 'warning' };
    default:
      return null;
  }
};

// Derives which top-level dashboard tab (Archiving vs Archived Files) the ?section=
// deep-link param points to, so ArchivingDashboard can react to it both on mount and
// on later navigation (e.g. the notification card's "See Result" button).
export const getArchivingTabFromSearch = (search: string): EArchivingDashboardTab =>
  new URLSearchParams(search).get(ARCHIVED_FILES_SECTION_PARAM) ===
  ARCHIVED_FILES_SECTION_VALUE
    ? EArchivingDashboardTab.ArchivedFiles
    : EArchivingDashboardTab.Archiving;

export type PreviewTableView = 'loading' | 'empty' | 'noResults' | 'table';

// empty: no path selected (prompt the user to choose archive parameters).
// noResults: a path is selected but it contains no matching experiments.
export const getPreviewTableView = ({
  isLoading,
  hasSelection,
  rowCount,
}: {
  isLoading: boolean;
  hasSelection: boolean;
  rowCount: number;
}): PreviewTableView => {
  if (!hasSelection) {
    return 'empty';
  }

  if (isLoading) {
    return 'loading';
  }

  if (rowCount === 0) {
    return 'noResults';
  }

  return 'table';
};

export const collectDescendantNodeIds = (nodes, targetId) => {
  const result = [];

  const gatherChildIds = (n) => {
    result.push(n.id);
    if (n.children && n.children.length) {
      n.children.forEach((c) => gatherChildIds(c));
    }
  };

  const searchForNode = (list) => {
    for (let i = 0; i < (list || []).length; i++) {
      const node = list[i];
      if (node.id === targetId) {
        gatherChildIds(node);
        return true;
      }
      if (node.children && node.children.length) {
        const found = searchForNode(node.children);
        if (found) {
          return true;
        }
      }
    }
    return false;
  };

  searchForNode(nodes);
  return result;
};

// Returns a new tree with `state.expanded = true` for every node whose id is in
// `idsToExpand`, leaving all other nodes (and untouched branches) referentially
// unchanged. Used to auto-expand a selected node's entire sub-tree in the archive
// explorer, whose BaseTree renders expansion from `node.state.expanded` (it uses a
// custom `shouldRenderNode`, so the explorer's internal expandedTreeNodeIds path
// does not apply).
export const expandNodesByIds = (nodes, idsToExpand: Set<number>) => {
  if (!Array.isArray(nodes) || !nodes.length || !idsToExpand.size) {
    return nodes;
  }

  let changed = false;
  const next = nodes.map((node) => {
    const nextChildren = node.children?.length
      ? expandNodesByIds(node.children, idsToExpand)
      : node.children;

    const shouldExpand = idsToExpand.has(node.id);
    if (!shouldExpand && nextChildren === node.children) {
      return node;
    }

    changed = true;
    return {
      ...node,
      children: nextChildren,
      state: shouldExpand ? { ...node.state, expanded: true } : node.state,
    };
  });

  return changed ? next : nodes;
};

// Collects the ids of every node that has at least one descendant whose name
// matches `searchText` (i.e. the ancestors on the path to each match). Used to
// auto-expand those branches once when the search term changes, so matched items
// deep in the tree become visible without the user expanding manually.
export const collectSearchMatchAncestorIds = (nodes, searchText: string): Set<number> => {
  const text = searchText.trim().toLowerCase();
  const ids = new Set<number>();

  if (!text) {
    return ids;
  }

  // Returns true when `node` or any of its descendants matches the search text.
  const visit = (node): boolean => {
    let subtreeHasMatch = !!node.name?.toLowerCase().includes(text);

    if (node.children?.length) {
      let childHasMatch = false;
      node.children.forEach((child) => {
        if (visit(child)) {
          childHasMatch = true;
        }
      });
      if (childHasMatch) {
        ids.add(node.id);
        subtreeHasMatch = true;
      }
    }

    return subtreeHasMatch;
  };

  (nodes || []).forEach(visit);
  return ids;
};

export type ArchivingNotificationView =
  | 'scheduled'
  | 'inProgress'
  | 'pausedStorage'
  | 'pausedByUser'
  | 'startingOver'
  | 'startedOver'
  | 'completed'
  | 'failed';

// Maps a job's data-object status string to the relevant paused view, or null when
// the job is not paused. Shared by the InProgress and BeforeStart branches below.
const getPausedView = (status: unknown): ArchivingNotificationView | null => {
  if (status === ARCHIVING_WAITING_FOR_STORAGE_STATUS) {
    return 'pausedStorage';
  }
  if (status === ARCHIVING_PAUSED_BY_USER_STATUS) {
    return 'pausedByUser';
  }
  return null;
};

// Returns the failure summary carried on a job's data object (e.g. the partial-failure
// message on an otherwise-completed job), or null when there is none.
export const getArchivingFailureSummary = (
  job: BackgroundJobDTO | null,
): string | null => {
  const progressData = normalizeJobDataObj(job?.dataObj) ?? {};
  const errorMessage = progressData.errorMessage ?? progressData.ErrorMessage;
  return typeof errorMessage === 'string' && errorMessage.length > 0
    ? errorMessage
    : null;
};

export const getArchivingNotificationView = (
  job: BackgroundJobDTO | null,
): ArchivingNotificationView | null => {
  if (!job) {
    return null;
  }

  const progressData = normalizeJobDataObj(job.dataObj) ?? {};
  const status = progressData.status ?? progressData.Status;

  // Start Over terminal: its own dismissable card (this is a Completed job status,
  // so it must be handled before the generic Completed → 'completed' mapping). It
  // persists until the user dismisses it or clicks Clear all, like other terminal cards.
  if (status === ARCHIVING_STARTED_OVER_STATUS) {
    return 'startedOver';
  }

  if (job.jobStatus === EJobStatus.Error) {
    return 'failed';
  }

  if (job.jobStatus === EJobStatus.Completed) {
    return 'completed';
  }

  if (job.jobStatus === EJobStatus.InProgress) {
    if (status === ARCHIVING_STARTING_OVER_STATUS) {
      return 'startingOver';
    }
    return getPausedView(status) ?? 'inProgress';
  }

  if (job.jobStatus === EJobStatus.BeforeStart) {
    const pausedView = getPausedView(status);
    if (pausedView) {
      return pausedView;
    }
    if (status === 'Pending') {
      return 'scheduled';
    }
  }

  return null;
};

export const DISMISSABLE_ARCHIVING_NOTIFICATION_VIEWS: ArchivingNotificationView[] = [
  'scheduled',
  'completed',
  'failed',
  'startedOver',
];

export const isArchivingNotificationDismissable = (job: BackgroundJobDTO | null) => {
  const view = getArchivingNotificationView(job);
  return view != null && DISMISSABLE_ARCHIVING_NOTIFICATION_VIEWS.includes(view);
};

export const collectIndeterminateNodeIds = (nodes, checkedIds: number[]): number[] => {
  const checkedSet = new Set(checkedIds);
  const indeterminateIds: number[] = [];

  const visit = (nodeList) => {
    for (const node of nodeList || []) {
      if (node.children?.length) {
        const descendantIds = collectDescendantNodeIds(nodes, node.id).filter(
          (id) => id !== node.id,
        );
        const checkedCount = descendantIds.filter((id) => checkedSet.has(id)).length;

        if (checkedCount > 0 && checkedCount < descendantIds.length) {
          indeterminateIds.push(node.id);
        }

        visit(node.children);
      }
    }
  };

  visit(nodes);
  return indeterminateIds;
};
